import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ClipboardList, Upload, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAdminStats, getAdminOrders } from '../../lib/api';
import { Toaster } from 'sonner';

export const AdminDashboard = () => {
  const { t } = useApp();
  const [stats, setStats] = useState({
    total_products: 0,
    pending_review: 0,
    total_orders: 0,
    pending_orders: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, ordersRes] = await Promise.all([
          getAdminStats(),
          getAdminOrders({ limit: 5 }),
        ]);
        setStats(statsRes.data);
        setRecentOrders(ordersRes.data.slice(0, 5));
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      pending_approval: 'status-pending',
      proforma_sent: 'status-processing',
      awaiting_wire: 'status-pending',
      paid: 'status-approved',
      processing: 'status-processing',
      shipped: 'status-approved',
    };
    return colors[status] || 'status-pending';
  };

  const formatStatus = (status) => {
    return t(`status.${status}`) || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-dashboard">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold">{t('adminDash.title')}</h1>
        <p className="text-white/60 mt-1">{t('adminDash.welcome')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Link to="/admin/products" className="card-dark p-6 group hover:border-[#00D4FF]/30 transition-all">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] group-hover:bg-[#00D4FF] group-hover:text-black transition-all">
              <Package className="w-6 h-6" />
            </div>
            <TrendingUp className="w-5 h-5 text-[#10B981]" />
          </div>
          <div className="text-3xl font-bold font-mono mb-1">{stats.total_products}</div>
          <div className="text-white/50 text-sm">{t('adminDash.totalProducts')}</div>
        </Link>

        <Link to="/admin/import" className="card-dark p-6 group hover:border-[#F59E0B]/30 transition-all">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#F59E0B]/10 text-[#F59E0B] group-hover:bg-[#F59E0B] group-hover:text-black transition-all">
              <Upload className="w-6 h-6" />
            </div>
            {stats.pending_review > 0 && <AlertCircle className="w-5 h-5 text-[#F59E0B]" />}
          </div>
          <div className="text-3xl font-bold font-mono mb-1">{stats.pending_review}</div>
          <div className="text-white/50 text-sm">{t('adminDash.pendingReview')}</div>
        </Link>

        <Link to="/admin/orders" className="card-dark p-6 group hover:border-[#10B981]/30 transition-all">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#10B981]/10 text-[#10B981] group-hover:bg-[#10B981] group-hover:text-white transition-all">
              <ClipboardList className="w-6 h-6" />
            </div>
            <CheckCircle className="w-5 h-5 text-[#10B981]" />
          </div>
          <div className="text-3xl font-bold font-mono mb-1">{stats.total_orders}</div>
          <div className="text-white/50 text-sm">{t('adminDash.totalOrders')}</div>
        </Link>

        <div className="card-dark p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#EF4444]/10 text-[#EF4444]">
              <AlertCircle className="w-6 h-6" />
            </div>
          </div>
          <div className="text-3xl font-bold font-mono mb-1">{stats.pending_orders}</div>
          <div className="text-white/50 text-sm">{t('adminDash.pendingOrders')}</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="card-dark p-6">
          <h3 className="text-lg font-semibold mb-4">{t('adminDash.quickActions')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <Link to="/admin/import" className="btn-primary text-center">
              {t('adminDash.importInventory')}
            </Link>
            <Link to="/admin/products" className="btn-secondary text-center">
              {t('adminDash.viewProducts')}
            </Link>
            <Link to="/admin/orders" className="btn-secondary text-center">
              {t('adminDash.manageOrders')}
            </Link>
            <Link to="/admin/settings" className="btn-secondary text-center">
              {t('adminNav.settings')}
            </Link>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="card-dark p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{t('adminDash.recentOrders')}</h3>
            <Link to="/admin/orders" className="text-[#00D4FF] text-sm hover:underline">
              {t('adminDash.viewAll')}
            </Link>
          </div>
          {recentOrders.length === 0 ? (
            <p className="text-white/40 text-center py-8">{t('adminDash.noOrdersYet')}</p>
          ) : (
            <div className="space-y-3">
              {recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/admin/orders/${order.id}`}
                  className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div>
                    <div className="font-mono text-sm text-[#00D4FF]">{order.order_number}</div>
                    <div className="text-white/60 text-xs">{order.company_name}</div>
                  </div>
                  <div className="text-right">
                    <div className={`status-badge ${getStatusColor(order.status)}`}>
                      {formatStatus(order.status)}
                    </div>
                    <div className="text-white/40 text-xs mt-1">
                      ${order.total_amount.toLocaleString()}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
